/**
 * Certificate Schemas (Browser-Safe)
 *
 * This file contains ONLY Zod schemas with ZERO Node.js dependencies.
 */

import { z } from 'zod';

export const CertificateInfoSchema = z.object({
  certificateId: z.string(),
  name: z.string(),
  description: z.string().nullable(),

  // Certificate data
  certificateData: z.string(),
  privateKeyData: z.string(),
  caCertificateData: z.string().nullable(),

  // Certificate metadata
  subjectDn: z.string(),
  issuerDn: z.string(),
  serialNumber: z.string(),
  fingerprint: z.string(),

  // Validity period - ISO timestamps
  validFrom: z.string(),
  validTo: z.string(),

  // Certificate properties
  keySize: z.number(),
  signatureAlgorithm: z.string(),
  publicKeyAlgorithm: z.string(),

  // SAN and configuration
  subjectAltNames: z.any().nullable(),
  certificateType: z.enum(['server', 'client', 'ca', 'intermediate']),

  // Teams-specific
  teamsCompatible: z.boolean(),
  teamsValidated: z.boolean(),
  certificateChain: z.any().nullable(),

  // Lifecycle
  autoRenew: z.boolean(),
  renewalThreshold: z.number(),

  // Usage tracking
  environment: z.string().nullable(),
  usageCount: z.number(),
  lastUsed: z.string().nullable(),

  // Metadata
  metadata: z.any().nullable(),

  // Timestamps - ISO strings for JSON compatibility
  createdAt: z.string(),
  updatedAt: z.string(),
  importedAt: z.string().nullable()
});

export type CertificateInfo = z.infer<typeof CertificateInfoSchema>;

export const PublicCertificateInfoSchema = CertificateInfoSchema.omit({ privateKeyData: true }).extend({ hasPrivateKey: z.boolean() });
export type PublicCertificateInfo = z.infer<typeof PublicCertificateInfoSchema>;

export function publicCertificateInfo(certificate: CertificateInfo): PublicCertificateInfo {
  const { privateKeyData, ...publicFields } = certificate;
  return { ...publicFields, hasPrivateKey: privateKeyData.trim().length > 0 };
}

export const CertificateImportInputSchema = z.object({
  certificateId: z.string().trim().min(1).max(100).optional(),
  name: z.string().trim().min(1).max(100),
  description: z.string().nullable().optional(),
  certificateType: z.enum(['server', 'client', 'ca', 'intermediate']),
  certificateData: z.string().trim().min(1),
  privateKeyData: z.string().nullable().optional(),
  caCertificateData: z.string().nullable().optional(),
  environment: z.string().trim().min(1).nullable().optional(),
});

export const CertificateCreateInputSchema = z.object({
  certificateId: z.string().min(1).max(100),
  name: z.string().min(1).max(100),
  description: z.string().optional(),

  // Certificate configuration
  certificateType: z.enum(['server', 'client', 'ca', 'intermediate']).default('server'),

  // Certificate data (PEM format)
  certificateData: z.string().min(1),
  privateKeyData: z.string().optional(),
  caCertificateData: z.string().optional(),

  // Certificate metadata (will be parsed from certificate)
  subjectDn: z.string().min(1),
  issuerDn: z.string().min(1),
  serialNumber: z.string().min(1),
  fingerprint: z.string().min(1),

  // Validity period
  validFrom: z.string().transform(str => new Date(str)),
  validTo: z.string().transform(str => new Date(str)),

  // Certificate properties
  keySize: z.number().default(2048),
  signatureAlgorithm: z.string().default('sha256WithRSAEncryption'),
  publicKeyAlgorithm: z.string().default('rsaEncryption'),
  subjectAltNames: z.array(z.string()).default([]),

  // Teams-specific
  teamsCompatible: z.boolean().default(false),

  // Environment
  environment: z.string().default('production')
}).superRefine((data, ctx) => {
  const requiresPrivateKey = data.certificateType === 'server' || data.certificateType === 'client';
  if (requiresPrivateKey && (!data.privateKeyData || data.privateKeyData.trim().length === 0)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['privateKeyData'],
      message: 'Private key is required for server and client certificates'
    });
  }
});

export type CertificateCreateInput = z.infer<typeof CertificateCreateInputSchema>;

export const CertificateUpdateInputSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  description: z.string().nullable().optional(),
  certificateType: z.enum(['server', 'client', 'ca', 'intermediate']).optional(),
  certificateData: z.string().optional(),
  privateKeyData: z.string().nullable().optional(),
  caCertificateData: z.string().nullable().optional(),
  subjectAltNames: z.array(z.string()).optional(),
  environment: z.string().optional(),
  autoRenew: z.boolean().optional(),
  teamsCompatible: z.boolean().optional()
});

export type CertificateUpdateInput = z.infer<typeof CertificateUpdateInputSchema>;

export const CertificateQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(1000).default(100),
  offset: z.coerce.number().int().min(0).default(0),
  certificateType: z.enum(['server', 'client', 'ca', 'intermediate']).optional(),
  domain: z.string().optional()
});

export type CertificateQuery = z.infer<typeof CertificateQuerySchema>;
